import type { JSX } from "react";
import { useCallback, useEffect, useRef } from "react";

import purify from "dompurify";
import styled from "styled-components";

import { colorsPalettes } from "@similarweb/styles";
import { FlexColumn, IconButton } from "@similarweb/ui-components";

import Button from "app/components/React/Button/Button";
import SWReactIcons from "app/components/React/SWReactIcons/SWReactIcons";
import { historyHookPreselect } from "app/contexts/AddonStateContext/const";
import { useNoTouchPurchaseContext } from "app/contexts/NoTouchPurchaseContext/NoTouchPurchaseContext";
import { allTrackers } from "app/services/track/track";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";

const StyledSWReactIcons = styled(SWReactIcons)`
    padding: 7px;
    border-radius: 20px;
    background-color: ${colorsPalettes.blue[100]};
`;

const StyledSpan = styled.span`
    color: ${colorsPalettes.carbon[400]};
    font-weight: 400;
    font-size: 14px;
    text-align: center;
`;

const PopoverWrapper = styled(FlexColumn)`
    z-index: 1000;
    position: absolute;
    right: 65px;
    top: 75px;

    align-items: center;

    border-radius: 6px;
    padding: 24px 16px;
    width: ${300 - 2 * 16}px;
    background-color: ${colorsPalettes.carbon[0]};
    color: ${colorsPalettes.carbon[400]};
    box-shadow:
        0 0 8px 0 ${`${colorsPalettes.black[0]}40`},
        0 8px 8px 0 rgba(0, 0, 0, 0.24);
`;

const StyledIconButton = styled(IconButton)`
    position: absolute;
    top: 8px;
    right: 8px;
`;

const SELECTED_PERIOD = 15;

interface IWWOTrafficVisitsOverTimePopoverProps {
    onCloseButtonClicked: () => void;
}

export const WWOTrafficVisitsOverTimePopover = ({
    onCloseButtonClicked,
}: IWWOTrafficVisitsOverTimePopoverProps): JSX.Element => {
    const translate = useTranslation();
    const { handlePurchaseAddonClick } = useNoTouchPurchaseContext();

    const popoverWrapper = useRef<HTMLDivElement>(null);

    // NOTE: The popover doesn't align well to the buttons in SwitcherGranularityContainer,
    // so we need to slightly shift them
    useEffect(() => {
        if (!popoverWrapper) {
            return;
        }

        popoverWrapper.current.parentElement.style.left = "12px";
    }, [popoverWrapper]);

    const onClick = useCallback(() => {
        allTrackers.dispatchTrackEvent("internal click", "click", "add-on/visits over time");
        handlePurchaseAddonClick(historyHookPreselect, SELECTED_PERIOD);
    }, [handlePurchaseAddonClick]);

    return (
        <PopoverWrapper
            ref={popoverWrapper}
            $gap="16px"
        >
            <StyledIconButton
                type="flat"
                onClick={onCloseButtonClicked}
                iconName="clear"
                placement="left"
                iconSize="xs"
            />
            <StyledSWReactIcons
                iconName={"calendar-locked"}
                size={"sm"}
                iconColor={colorsPalettes.blue[400]}
            />

            <StyledSpan
                dangerouslySetInnerHTML={{
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    __html: purify.sanitize(translate("website_analysis.visits_over_time.popover_text")),
                }}
            />

            <Button
                style={{ width: "100%" }}
                label={translate("website_analysis.visits_over_time.popover_button")}
                onClick={onClick}
            />
        </PopoverWrapper>
    );
};
